import { useToastManager } from "@porsche-design-system/components-react"
import type { NotesView } from "@slfinrtl/ui-bff-routes"
import { changeNotesRoute, getNotesViewRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { AnimatedStage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useBffDynamic } from "src/hooks"
import { useVehicleDetailsContextForStage } from "../../context/VehicleDetailsContext"
import { useEditActionButton } from "../../hooks/useEditActionButton"
import { Notes } from "./components/Notes"
import { NotesForm } from "./components/NotesForm"
import { NotesSkeleton } from "./components/NotesSkeleton"

const messages = defineMessages({
	headline: {
		defaultMessage: "Notes",
		id: "vehicleDetails.notes.headline",
	},
})

export const NotesContainer = withErrorHandler(() => {
	const [view, setView] = useState<NotesView | null>()
	const [isSaving, setIsSaving] = useState(false)
	const { addMessage } = useToastManager()
	const { inventoryId, mode, setEditStage } = useVehicleDetailsContextForStage("notes")

	const fetchView = useBff(
		getNotesViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const saveNotes = useBffDynamic(changeNotesRoute, "inline")
	const { formatMessage } = useIntl()

	useEffect(() => {
		fetchView()
			.then(setView)
			.catch(() => setView(null))
	}, [fetchView])

	const handleSave = async (value: string | undefined) => {
		setIsSaving(true)
		await saveNotes({
			body: { value: value?.trim() === "" ? undefined : value },
			pathParams: { inventoryId },
			queryParams: {},
		})
			.then((notesView: NotesView) => {
				setView(notesView)
				setEditStage(undefined)
				addMessage({
					state: "success",
					text: formatMessage(commonMessages.saveSuccess),
				})
			})
			.finally(() => setIsSaving(false))
	}

	const actionButton = useEditActionButton("notes", view?.editAction, undefined)

	return (
		<AnimatedStage title={formatMessage(messages.headline)} actionButtons={actionButton}>
			<ErrorNotificationInline dismissButton={false} />
			{view ? (
				mode !== "edit" ? (
					<Notes view={view} />
				) : (
					<NotesForm
						initialValue={view.value}
						isLoading={isSaving}
						onSave={handleSave}
						onCancel={() => setEditStage(undefined)}
					/>
				)
			) : view === undefined ? (
				<NotesSkeleton />
			) : null}
		</AnimatedStage>
	)
})
